import { Accordion, Alert, Anchor, Badge, Button, Code, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import CompositionRunLink from './CompositionRunLink.tsx';
import { useCompositionReadiness } from '@/hooks/api/automations/useExecutionCompositions.ts';
import { usePublicationMutations } from '@/hooks/api/automations/useAutomationPublications.ts';
import type { CompositionRecord, Identity, Manifest } from '@/types/executionCompositions.ts';
import BaselineApproval from './BaselineApproval.tsx';
import ExactCompositionTest from './ExactCompositionTest.tsx';
import { ExactDetails, ReleaseSection } from './CompositionPresentation.tsx';
import { proofKindLabel } from './compositionPresentation.ts';
import AcceptanceCampaignEvidence from './AcceptanceCampaignEvidence.tsx';

export default function CompositionEvidence({
  releaseId,
  manifest,
  lifecycleRevision,
  identity,
  canEdit = false,
  status,
  onChanged,
}: {
  releaseId: string;
  manifest: Manifest;
  lifecycleRevision: number;
  identity: Identity;
  canEdit?: boolean;
  status?: CompositionRecord['status'];
  onChanged?: () => void;
}) {
  const readiness = useCompositionReadiness(releaseId);
  const { freeze } = usePublicationMutations();
  const [freezeError, setFreezeError] = useState('');
  if (readiness.isError)
    return (
      <Alert color="red">
        Evidence unavailable.{' '}
        <Anchor onClick={() => void readiness.refetch()}>Refresh evidence</Anchor>
      </Alert>
    );
  if (!readiness.data || !readiness.isFetchedAfterMount)
    return <Text>Loading qualified evidence…</Text>;
  const proof = readiness.data;
  if (
    proof.release_id !== releaseId ||
    proof.lifecycle_revision !== lifecycleRevision ||
    proof.composition_digest !== identity.composition_digest ||
    proof.resource_digest !== identity.resource_digest ||
    proof.documents.documentation_digest !== identity.documentation_digest ||
    (proof.delivery_assessment != null &&
      (proof.delivery_assessment.release_id !== releaseId ||
        proof.delivery_assessment.composition_digest !== identity.composition_digest ||
        proof.delivery_assessment.resource_digest !== identity.resource_digest))
  )
    return (
      <Alert color="orange">
        Release changed. Refresh the selected candidate before reviewing evidence.
      </Alert>
    );
  const baselineGroups = manifest.requirements.reduce<
    {
      baseline: NonNullable<Manifest['requirements'][number]['declaration']['comparison_baseline']>;
      scopes: string[];
    }[]
  >((groups, requirement) => {
    const declaration = requirement.declaration;
    const baseline = declaration.comparison_baseline;
    if (
      declaration.kind !== 'comparison' ||
      !baseline ||
      !declaration.comparison ||
      !['matched', 'mismatch'].includes(declaration.comparison.expected_result)
    )
      return groups;
    const existing = groups.find(
      (group) =>
        group.baseline.evidence_ref === baseline.evidence_ref &&
        group.baseline.content_sha256 === baseline.content_sha256 &&
        group.baseline.authority_scope === baseline.authority_scope,
    );
    if (existing) existing.scopes.push(declaration.scope);
    else groups.push({ baseline, scopes: [declaration.scope] });
    return groups;
  }, []);
  return (
    <ReleaseSection
      title="3. Checks and completion"
      description="Review the exact saved Release, its checks, and customer acceptance separately. Publication remains a separate action."
    >
      <Group>
        <Badge color={readiness.isFetching ? 'blue' : proof.technical_ready ? 'green' : 'orange'}>
          Technical evidence{' '}
          {readiness.isFetching ? 'refreshing' : proof.technical_ready ? 'qualified' : 'incomplete'}
        </Badge>
        <Text size="sm">Customer acceptance and publication are separate.</Text>
      </Group>
      {proof.delivery_assessment && (
        <Stack gap={2}>
          <Text size="sm">Build: exact saved Release</Text>
          {proof.delivery_assessment.workflow ? (
            <>
              <Text size="sm">
                Component checks: {proof.delivery_assessment.workflow.local_packages_ready}/
                {proof.delivery_assessment.workflow.member_count} local packages ready
                {' · '}
                {proof.delivery_assessment.workflow.v2_members_passed}/
                {proof.delivery_assessment.workflow.member_count} V2 checks passed
              </Text>
              <Text size="sm">
                Checks: {proof.delivery_assessment.workflow.satisfied_requirements}/
                {proof.delivery_assessment.workflow.declared_requirements} declared requirements
                satisfied
              </Text>
            </>
          ) : (
            <Text size="sm">
              Component and requirement summary unavailable in this observation. Inspect the exact
              evidence below.
            </Text>
          )}
          <Text size="sm">
            Completion: {proof.delivery_assessment.acceptance_campaign.status.replaceAll('_', ' ')}{' '}
            · {proof.delivery_assessment.acceptance_campaign.case_count} configured cases
          </Text>
          <Text size="sm">
            Historical workbook comparisons:{' '}
            {proof.delivery_assessment.acceptance_campaign.native_comparisons_passed}/
            {proof.delivery_assessment.acceptance_campaign.native_comparisons_required}
            {' · '}Human artifact reviews:{' '}
            {proof.delivery_assessment.acceptance_campaign.human_reviews_accepted}/
            {proof.delivery_assessment.acceptance_campaign.human_reviews_required}
          </Text>
          {proof.delivery_assessment.acceptance_campaign.status !== 'accepted' && (
            <Text size="sm">Technical readiness does not complete the acceptance campaign.</Text>
          )}
          {proof.delivery_assessment.next_actions?.map((action) => (
            <Text key={action.code} size="sm">
              Next: {action.message}
            </Text>
          ))}
          {proof.delivery_assessment.limits?.map((limit) => (
            <Text key={limit} size="sm" c="dimmed">
              {limit}
            </Text>
          ))}
          <ExactDetails
            summary="Exact observation identity"
            values={{
              'Release ID': releaseId,
              'Composition digest': proof.composition_digest,
              'Resource digest': proof.resource_digest,
              'Observed at': proof.delivery_assessment.workflow?.observed_at ?? 'Unavailable',
              'Campaign ID':
                proof.delivery_assessment.acceptance_campaign.campaign_id ?? 'Not configured',
            }}
          />
        </Stack>
      )}
      {proof.blockers.map((message, index) => (
        <Alert key={index} color="orange">
          {message}
        </Alert>
      ))}
      {baselineGroups.map(({ baseline, scopes }) => {
        const approval = (proof.baseline_approvals ?? []).find(
          (item) =>
            item.baseline.evidence_ref === baseline.evidence_ref &&
            item.baseline.content_sha256 === baseline.content_sha256 &&
            item.baseline.authority_scope === baseline.authority_scope,
        );
        return (
          <BaselineApproval
            key={`${baseline.evidence_ref}/${baseline.content_sha256}`}
            releaseId={releaseId}
            lifecycleRevision={lifecycleRevision}
            baseline={baseline}
            approval={approval}
            scopes={scopes}
            canEdit={canEdit}
            status={status}
            onChanged={() => {
              void readiness.refetch();
              onChanged?.();
            }}
          />
        );
      })}
      <AcceptanceCampaignEvidence
        key={releaseId}
        releaseId={releaseId}
        summary={proof.delivery_assessment?.acceptance_campaign}
        canEdit={canEdit}
        onChanged={() => {
          void readiness.refetch();
          onChanged?.();
        }}
      />
      {canEdit && status === 'draft' && (
        <Stack gap="xs">
          <Text size="sm">
            Freeze this saved configuration before running version-bound checks.
          </Text>
          <Button
            disabled={!proof.can_freeze || freeze.isPending}
            loading={freeze.isPending}
            onClick={() => {
              setFreezeError('');
              void freeze
                .mutateAsync({ releaseId, revision: lifecycleRevision })
                .catch((cause) => setFreezeError(getHttpErrorMessage(cause)));
            }}
          >
            Freeze saved configuration
          </Button>
          {freezeError && <Alert color="red">{freezeError}</Alert>}
        </Stack>
      )}
      {!manifest.requirements.length && (
        <Alert color="orange">
          No version-bound tests are declared. You can still freeze and publish a valid Release.
        </Alert>
      )}
      <Accordion keepMounted keepMountedMode="display-none">
        {manifest.requirements.map((requirement) => {
          const declaration = requirement.declaration;
          const evidence = readiness.isFetching
            ? undefined
            : proof.requirements.find((item) => item.requirement_id === declaration.requirement_id);
          return (
            <Accordion.Item key={declaration.requirement_id} value={declaration.requirement_id}>
              <Stack gap="xs" py="sm">
                <Group>
                  <Text fw={600}>{declaration.scope}</Text>
                  <Badge>{proofKindLabel[declaration.kind]}</Badge>
                  <Badge variant="outline" color="gray">
                    {declaration.required_for_publication
                      ? 'Declared test evidence'
                      : 'Optional evidence'}
                  </Badge>
                  <Badge>{(evidence?.status ?? 'missing').replaceAll('_', ' ')}</Badge>
                </Group>
                <Text size="sm">
                  Expected:{' '}
                  {declaration.outcome === 'unavailable'
                    ? 'explicitly unavailable output'
                    : declaration.outcome === 'produced'
                      ? 'produced artifact'
                      : 'structured result'}{' '}
                  · case <Code>{declaration.evidence.case_id}</Code>
                </Text>
                {evidence?.explanation && <Text>{evidence.explanation}</Text>}
                {evidence?.run_id && (
                  <CompositionRunLink runId={evidence.run_id}>
                    Inspect evidence Run {evidence.run_id}
                  </CompositionRunLink>
                )}
                {evidence?.artifact && (
                  <ExactDetails
                    summary="Artifact identity"
                    values={{
                      'Artifact ID': evidence.artifact.dataset_id,
                      'Artifact SHA-256': evidence.artifact.artifact_sha256,
                    }}
                  />
                )}
                {evidence?.comparison && (
                  <Alert
                    color={
                      evidence.comparison.result === 'matched' &&
                      evidence.comparison.baseline_eligibility === 'eligible'
                        ? 'green'
                        : 'orange'
                    }
                  >
                    Comparison: {evidence.comparison.result.replaceAll('_', ' ')} · baseline{' '}
                    {evidence.comparison.baseline_eligibility}
                    {evidence.comparison.explanation && (
                      <Text>{evidence.comparison.explanation}</Text>
                    )}
                    {evidence.comparison.reported_actual_artifact && (
                      <Text size="sm">
                        Actual producer Run{' '}
                        <Code>{evidence.comparison.reported_actual_artifact.run_id}</Code> ·
                        artifact{' '}
                        <Code>{evidence.comparison.reported_actual_artifact.dataset_id}</Code>
                      </Text>
                    )}
                  </Alert>
                )}
                {evidence?.reused && (
                  <Text size="sm">
                    Requalified evidence from Release <Code>{evidence.source_release_id}</Code>; no
                    new execution is claimed.
                  </Text>
                )}
              </Stack>
              <Accordion.Control>Test requirement: {declaration.scope}</Accordion.Control>
              <Accordion.Panel>
                <ExactCompositionTest
                  releaseId={releaseId}
                  requirement={requirement}
                  identity={identity}
                  onEvidenceChange={() => void readiness.refetch()}
                />
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </ReleaseSection>
  );
}
